import { ColDef } from 'ag-grid-community';
import { IntlShape } from 'react-intl';
import { RIGHT_ALIGNED, RIGHT_ALIGNED_STYLES } from 'common/constants';
import { RemittanceFormRenderer } from 'components/RemittanceFormRenderer';
import { formatCurrency } from 'utils/formatCurrency';
import { formatLocaleDate } from '../../utils/dateUtils';
import { ReconciliationColumnFields } from './constants';
import { labelMessages } from './messages';
import { OptionType } from './types';
import { VoidPaymentDialog } from './VoidPaymentDialog';

const hideColumn = (filteredOptions: OptionType[], columnName: string) =>
  !filteredOptions?.some(item => item.value === columnName);

export const reconciliationReportColumns = (
  intl: IntlShape,
  filteredOptions: OptionType[]
): ColDef[] => [
  {
    field: 'check',
    filter: false,
    headerName: '',
    maxWidth: 70,
    resizable: false,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    cellStyle: {
      padding: '0 0 0 2rem'
    },
    headerClass: 'checkbox-header',
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.Status,
    headerName: intl.formatMessage(labelMessages.statusLabel),
    sortable: true,
    maxWidth: 100,
    resizable: true,
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.DocumentId,
    headerName: intl.formatMessage(labelMessages.documentIdLabel),
    resizable: true,
    maxWidth: 120,
    cellRendererParams: {
      isRCC: true
    },
    suppressSizeToFit: true,
    cellRenderer: RemittanceFormRenderer
  },
  {
    field: ReconciliationColumnFields.PayableId,
    headerName: intl.formatMessage(labelMessages.payableIdLabel),
    resizable: true,
    maxWidth: 120,
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.CardNumber,
    headerName: intl.formatMessage(labelMessages.cardNoLabel),
    resizable: true,
    maxWidth: 150,
    hide: hideColumn(filteredOptions, ReconciliationColumnFields.CardNumber),
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.VendorId,
    headerName: intl.formatMessage(labelMessages.vendorNoLabel),
    resizable: true,
    maxWidth: 120,
    suppressSizeToFit: true,
    hide: hideColumn(filteredOptions, ReconciliationColumnFields.VendorId)
  },
  {
    field: ReconciliationColumnFields.VendorName,
    headerName: intl.formatMessage(labelMessages.vendorNameLabel),
    resizable: true,
    maxWidth: 200,
    suppressSizeToFit: true,
    hide: hideColumn(filteredOptions, ReconciliationColumnFields.VendorName)
  },
  {
    field: ReconciliationColumnFields.PayableAmount,
    headerName: intl.formatMessage(labelMessages.paymentAmountLabel),
    resizable: true,
    maxWidth: 140,
    type: RIGHT_ALIGNED,
    cellStyle: RIGHT_ALIGNED_STYLES,
    valueFormatter: ({ value }) => formatCurrency(value, 'decimal'),
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.SettledAmount,
    headerName: intl.formatMessage(labelMessages.settledAmountLabel),
    resizable: true,
    maxWidth: 140,
    type: RIGHT_ALIGNED,
    cellStyle: RIGHT_ALIGNED_STYLES,
    hide: hideColumn(filteredOptions, ReconciliationColumnFields.SettledAmount),
    valueFormatter: ({ value }) => formatCurrency(value, 'decimal'),
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.PaymentDate,
    headerName: intl.formatMessage(labelMessages.paymentDateLabel),
    resizable: true,
    maxWidth: 150,
    hide: hideColumn(filteredOptions, ReconciliationColumnFields.PaymentDate),
    valueFormatter: ({ value }) => formatLocaleDate(value, intl.locale),
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.TransactionDate,
    headerName: intl.formatMessage(labelMessages.transactionDateLabel),
    resizable: true,
    maxWidth: 150,
    hide: hideColumn(
      filteredOptions,
      ReconciliationColumnFields.TransactionDate
    ),
    valueFormatter: ({ value }) => formatLocaleDate(value, intl.locale),
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.SettledDate,
    headerName: intl.formatMessage(labelMessages.settledDateLabel),
    resizable: true,
    maxWidth: 150,
    hide: hideColumn(filteredOptions, ReconciliationColumnFields.SettledDate),
    valueFormatter: ({ value }) => formatLocaleDate(value, intl.locale),
    suppressSizeToFit: true
  },
  {
    field: ReconciliationColumnFields.Void,
    headerName: intl.formatMessage(labelMessages.voidLabel),
    resizable: true,
    maxWidth: 120,
    cellRenderer: VoidPaymentDialog,
    suppressSizeToFit: true
  }
];
